import { Box, Flex, Tooltip, Typography, SpaceScale } from '@fullstory/components';
import { useElementSize } from '@fullstory/react-hooks';
import React, {
  useEffect,
  useState,
  useRef,
  ReactElement,
  ComponentProps,
  PropsWithChildren,
} from 'react';
import { SegmentDot } from '../SegmentDot';
import { VisualizationLegendItemProps } from '../../types/types';
import css from './index.module.scss';
import { FormattedMessage } from 'react-intl';

const MEDIUM_CARD_WIDTH = 300;
const MARGIN_RIGHT = 20;
const MIN_LABEL_WIDTH = 60;
const ELLIPSIS_WIDTH = 28;

export interface VisualizationLegendProps {
  minLabelWidth?: number;
  justifyContent?: ComponentProps<typeof Flex>['justifyContent'];
  paddingTop?: SpaceScale;
  children:
    | ReactElement<VisualizationLegendItemProps>[]
    | ReactElement<VisualizationLegendProps>
    | undefined;
}

export const VisualizationLegend: React.FC<VisualizationLegendProps> = ({
  minLabelWidth = MIN_LABEL_WIDTH,
  justifyContent = 'center',
  paddingTop,
  children,
}) => {
  const { elementSize, ref } = useElementSize();

  // Convert single children to an array, and ignore any children that aren't VisualizationLegendItemProps
  const childArray = React.Children.toArray(children)
    .map(child => child as ReactElement<VisualizationLegendItemProps>)
    .filter(child => Object.keys(child.props).length > 0);

  const [labelsOnLegend, setLabelsOnLegend] = useState(childArray.length);
  const legendWidth = elementSize?.width ?? MEDIUM_CARD_WIDTH;
  const childrenToShow = childArray.slice(0, labelsOnLegend);
  const childrenToHide = childArray.slice(labelsOnLegend);
  const showEllipsis = childrenToHide.length > 0;

  // Calculate the labelWidth based on the number of labels; Allows for more width on cards with fewer labels
  // labelWidth must be positive
  const labelWidth = labelsOnLegend
    ? Math.abs(legendWidth / labelsOnLegend - (showEllipsis ? ELLIPSIS_WIDTH : 0))
    : 0;

  useEffect(() => {
    // show as many labels as possible with a minimun label width
    setLabelsOnLegend(
      Math.min(
        Math.floor((legendWidth - ELLIPSIS_WIDTH) / (minLabelWidth + MARGIN_RIGHT)),
        childArray.length,
      ),
    );
  }, [childArray.length, legendWidth, minLabelWidth]);

  return (
    <Box data-component="VisualizationLegend" paddingTop={paddingTop ?? 12} ref={ref}>
      <Flex
        flexWrap="nowrap"
        justifyContent={justifyContent}
        alignItems="center"
        className={css.legend}
      >
        {childrenToShow.map((child, i) => (
          <div
            key={`${child.props.label}${child.props.previous ? '-previous' : ''}`}
            style={{
              width: showEllipsis ? '100%' : 'auto',
              marginRight: `${MARGIN_RIGHT}px`,
              maxWidth: `${labelWidth}px`,
              minWidth: `${minLabelWidth}px`,
            }}
          >
            <TooltipWrapper>{child}</TooltipWrapper>
          </div>
        ))}
        {showEllipsis && (
          <Tooltip
            variant="light"
            placement="bottom"
            tooltipContent={
              <Box margin={[12, 8, 0, 8]}>
                {childrenToHide.map((child, i) => (
                  <Box key={`${child.props.label}`} marginBottom={12}>
                    {React.cloneElement<VisualizationLegendItemProps>(child, {
                      ...child.props,
                      tooltipVariant: true,
                    })}
                  </Box>
                ))}
              </Box>
            }
          >
            <Box padding={[0, 8, 8, 8]}>
              <Typography variant="body3" fontWeight="bold">
                <FormattedMessage
                  defaultMessage="..."
                  id="rXmv3S"
                  description="Ellipses displayed when more content is available"
                />
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Flex>
      <Flex
        flexWrap="wrap"
        justifyContent={justifyContent}
        alignItems="center"
        className={css.printLegend}
        gap={8}
      >
        {childArray.map(child => (
          <div key={`${child.props.label}${child.props.previous ? '-previous' : ''}-print`}>
            {child}
          </div>
        ))}
      </Flex>
    </Box>
  );
};

const TooltipWrapper: React.FC<{
  children: React.ReactElement<VisualizationLegendItemProps>;
}> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { elementSize, ref: sizeRef } = useElementSize();
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const labelBody = ref.current?.querySelector('.legend-label') as HTMLDivElement;
    setShowTooltip(labelBody ? labelBody.scrollWidth > labelBody.offsetWidth : false);
  }, [showTooltip, setShowTooltip, elementSize?.width]);

  return (
    <Flex
      data-component="TooltipWrapper"
      justifyContent="flex-start"
      className={css.noWrap}
      ref={sizeRef}
    >
      <Tooltip
        placement="bottom"
        tooltipContent={React.cloneElement<VisualizationLegendItemProps>(children, {
          ...children.props,
          tooltipVariant: true,
          invertColors: true,
        })}
        disabled={!showTooltip || children.props.disableTooltip}
        className={`${css.fullWidth}`}
      >
        <Box flex={1} ref={ref}>
          {children}
        </Box>
      </Tooltip>
    </Flex>
  );
};

export const VisualizationLegendBaseItem: React.FC<
  PropsWithChildren<{
    color: ComponentProps<typeof SegmentDot>['color'];
    variant: 'secondary' | 'primary';
  }>
> = ({ color, variant, children }) => (
  <Flex data-component="VisualizationLegendBaseItem" justifyContent="flex-start">
    <SegmentDot color={color} variant={variant} />
    <div className={`legend-label ${css.fullWidth}`}>{children}</div>
  </Flex>
);
